import React from "react";
import FlexBox from "../../../../FlexBox/FlexBox";
import Typography from "../../../../../Foundation/Typography/Typography";
import { ImageResponsive } from "../../../../Image";
import { ImageFit } from "../../../../Image/interface/ImageData";
import { _InnovationProductPetitPrinceRoot } from "./InnovationProductPetitPrince.styled";
import Button from "../../../../../Foundation/Button/Button";
import { ILandingInnovationPetitPrinceProduct } from "../LandingInnovationPetitPrince";
import StyledDiv from "../../../../StyledDiv/StyledDiv";
import htmlParse from "html-react-parser";
import { rem } from "../../../../../Utils/font";
import {
  ParallaxImage,
  ParallaxImageProps,
} from "../../../../Animations/ParallaxImage/ParallaxImage";
import useBreakpoint from "../../../../Grid/hooks/useBreakpoint";
import FadeIn from "../../../../Animations/FadeIn";
import { IImageResponsiveProps } from "../../../../Image/ImageResponsive";

export interface IInnovationProductPetitPrinceProps<T extends any = any> {
  product: ILandingInnovationPetitPrinceProduct<T>;
  className?: string;
  onClick?: (product: ILandingInnovationPetitPrinceProduct<T>) => void;
  compactComposition?: boolean;
}

const InnovationProductPetitPrince = <T extends any = any>(
  props: IInnovationProductPetitPrinceProps<T>
) => {
  const { product, className, onClick, compactComposition = false } = props;
  const { mobile } = useBreakpoint();

  const imageId = product.imageId;
  const staticImageSrc = product.staticImageSrc;
  const ctaLabel = product.ctaLabel || "Je commande";

  // Format composition: convert comma-separated list to pipe-separated
  const formatComposition = (composition?: string) => {
    if (!composition) return "";
    return composition
      .split(",")
      .map((item) => item.trim().toUpperCase())
      .join(" | ");
  };
  const desktopAspectRatio = "664 / 728";
  const mobileAspectRatio = "375 / 512";

  const descriptionColor =
    product.descriptionColor || product.color || "#543518";

  // Memoize parallax props to prevent ParallaxImage from re-initializing.
  // startYPercent: 0 when section enters so the image fills the container (no white gap at bottom).
  // endYPercent: negative so the image moves up as the section scrolls up (parallax).
  const parallaxImageProps: ParallaxImageProps = React.useMemo(
    () => ({
      parallax: true,
      startYPercent: mobile ? -7 : -30,
      endYPercent: mobile ? 13 : 30,
      transformOrigin: "center center",
      scale: mobile ? 1.1 : 1.15,
    }),
    [mobile]
  );

  const handleClick = React.useCallback(() => {
    onClick?.(product);
  }, [onClick, product]);

  // Memoize the static image to prevent ParallaxImage children from changing
  const staticImageElement = React.useMemo(
    () => (
      <StyledDiv
        as="img"
        src={staticImageSrc}
        alt={product.title}
        styles={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center",
          mobile: {
            width: "120%",
            objectPosition: "left center",
          },
        }}
      />
    ),
    [staticImageSrc, product.title]
  );

  // Memoize the ImageResponsive props to prevent re-renders
  const imageResponsiveProps: IImageResponsiveProps = React.useMemo(
    () => ({
      alt: product.title,
      className: "pp-image",
      desktop: {
        image: {
          id: imageId,
          fit: ImageFit.COVER,
          quality: 80,
        },
        wrapperStyle: {
          width: "100%",
          height: "100%",
          borderRadius: 0,
          overflow: "hidden",
          aspectRatio: desktopAspectRatio,
        },
        imgStyle: {
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center",
        },
      },
      mobile: {
        image: {
          id: imageId,
          fit: ImageFit.COVER,
          quality: 100,
          outputPixelDensities: [1, 2],
        },
        wrapperStyle: {
          width: "100%",
          height: "100%",
          borderRadius: 0,
          overflow: "hidden",
          aspectRatio: mobileAspectRatio,
        },
        imgStyle: {
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "left center",
        },
      },
    }),
    [imageId, product.title, desktopAspectRatio, mobileAspectRatio]
  );

  return (
    <StyledDiv
      as={FlexBox}
      className={className}
      styles={{
        width: "100%",
        overflow: "hidden",
        position: "relative",
        minHeight: "400px",
        aspectRatio: desktopAspectRatio,
        mobile: {
          minHeight: "136vw", // 136vw is the aspect ratio of the image 375 / 512
          aspectRatio: "unset", // unset is the aspect ratio of the image
        },
      }}
    >
      {/* Background Image */}
      {staticImageSrc ? (
        <StyledDiv
          styles={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            overflow: "hidden",
          }}
        >
          <ParallaxImage {...parallaxImageProps}>
            {staticImageElement}
          </ParallaxImage>
        </StyledDiv>
      ) : imageId && imageId !== "-1" && imageId !== "0" ? (
        <StyledDiv
          styles={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            overflow: "hidden",
            mobile: {
              ".ro-image-responsive": {
                height: "100%",
              },
              ".pp-image": {
                width: "132%",
                maxWidth: "132%",
                height: "100%",
                objectPosition: "left center",
                aspectRatio: "unset",
              },
            },
          }}
        >
          <ParallaxImage {...parallaxImageProps}>
            <ImageResponsive {...imageResponsiveProps} />
          </ParallaxImage>
        </StyledDiv>
      ) : null}

      {/* Content Overlay */}
      <StyledDiv
        as={FlexBox}
        direction="column"
        gap={24}
        styles={{
          position: "relative",
          zIndex: 1,
          height: "100%",
          padding: "38px 30px",
          // justifyContent: "space-between",
          maxWidth: "55%",
          gap: 24,
          mobile: {
            maxWidth: "73%",
            gap: 14,
            padding: "32px 24px",
          },
        }}
      >
        {/* Product Name */}
        <FadeIn delay={0.2}>
          <Typography
            variant="petitPrince1"
            color={product.color ? () => product.color || "" : undefined}
          >
            {product.title}
          </Typography>
        </FadeIn>

        <StyledDiv
          as={FlexBox}
          styles={{
            maxWidth: "67%",
            mobile: {
              maxWidth: "76%",
            },
          }}
          direction="column"
          gap={16}
        >
          {/* Description */}
          {product.description && (
            <FadeIn delay={0.3}>
              <Typography
                variant="body2"
                style={{
                  color: descriptionColor,
                  maxWidth: "94%",
                }}
              >
                {htmlParse((product.description || "") + "")}
              </Typography>
            </FadeIn>
          )}

          {/* Composition */}
          {product.composition && (
            <FadeIn delay={0.3}>
              <StyledDiv
                as={Typography}
                variant="subtitle2"
                styles={{
                  color: descriptionColor,
                  textTransform: "uppercase",
                  fontSize: rem(13),
                  fontStyle: "normal",
                  fontWeight: 800,
                  lineHeight: "140%",
                  mobile: {
                    maxWidth: compactComposition ? "90%" : undefined,
                  },
                }}
              >
                {formatComposition(product.composition)}
              </StyledDiv>
            </FadeIn>
          )}
        </StyledDiv>

        <StyledDiv
          as={FlexBox}
          styles={{ marginTop: "auto" }}
          direction="column"
          gap={16}
        >
          {/* Price and Quantity */}
          {((product.displayPrice && product.price) ||
            product.priceSubtitle) && (
            <FadeIn delay={0.3} start="top bottom">
              <FlexBox direction="column" gap={4} wrap="wrap">
                {product.displayFromMention && product.price && (
                  <Typography
                    variant="ui4"
                    style={{
                      marginTop: 16,
                      color: descriptionColor,
                    }}
                  >
                    {product.displayFromMention}
                  </Typography>
                )}
                {product.price || product.priceSubtitle ? (
                  <FlexBox
                    style={{ height: 25, marginTop: "auto" }}
                    gap={8}
                    wrap="wrap"
                  >
                    {product.price && (
                      <Typography
                        variant="subtitle2"
                        style={{
                          color: descriptionColor,
                        }}
                      >
                        {product.price}
                      </Typography>
                    )}
                    {product.priceSubtitle && product.price && (
                      <span style={{ color: descriptionColor }}>|</span>
                    )}
                    {product.priceSubtitle && (
                      <Typography
                        variant="subtitle2"
                        style={{
                          color: descriptionColor,
                        }}
                      >
                        {product.priceSubtitle}
                      </Typography>
                    )}
                  </FlexBox>
                ) : null}
              </FlexBox>
            </FadeIn>
          )}

          {/* Button */}
          <FadeIn delay={0.2} start="top bottom">
            <Button
              variant="champagne"
              style={{
                width: "fit-content",
              }}
              customize={{
                bg: product.color || "#543518",
                color: "#ffffff",
                border: product.color,
                hoverBg: product.color + "EE",
              }}
              onClick={handleClick}
            >
              {ctaLabel}
            </Button>
          </FadeIn>
        </StyledDiv>
      </StyledDiv>
    </StyledDiv>
  );
};

export default React.memo(
  InnovationProductPetitPrince
) as typeof InnovationProductPetitPrince;
